import { useLingui } from '@lingui/react';
import { useMemo } from 'react';
import {
  getActiveDescriptors,
  type ImporterDescriptor,
} from '../../descriptors';

type Weighted = { descriptor: ImporterDescriptor; score: number };

const WEIGHTS = {
  intent: 8,
  title: 5,
  shortTitle: 4,
  whenToUse: 2,
  keyword: 3,
  synonym: 1,
};

function normalize(s: string): string {
  return s.toLowerCase().trim();
}

function tokens(s: string): string[] {
  return normalize(s).split(/\s+/).filter(Boolean);
}

function scoreFieldAgainstQuery(field: string, query: string): number {
  const f = normalize(field);
  const q = normalize(query);
  if (!f || !q) return 0;
  if (f === q) return 1;
  if (f.startsWith(q)) return 0.85;
  if (f.includes(q)) return 0.7;
  const qToks = tokens(q);
  const fToks = tokens(f);
  if (!qToks.length) return 0;
  let matched = 0;
  for (const qt of qToks) {
    if (fToks.some((ft) => ft.startsWith(qt) || ft.includes(qt))) matched++;
  }
  if (matched === 0) return 0;
  return 0.5 * (matched / qToks.length);
}

export function useDescriptorSearch(query: string) {
  const { i18n } = useLingui();
  const active = useMemo(() => getActiveDescriptors(), []);

  return useMemo<ImporterDescriptor[]>(() => {
    const q = normalize(query);
    if (!q) {
      const byCategory: Record<string, number> = {
        'product-data': 0,
        'master-list': 1,
        other: 2,
      };
      return [...active].sort(
        (a, b) => byCategory[a.category] - byCategory[b.category]
      );
    }

    const scored: Weighted[] = active.map((d) => {
      let score = 0;
      for (const intent of d.intents) {
        score += WEIGHTS.intent * scoreFieldAgainstQuery(intent, q);
      }
      score += WEIGHTS.title * scoreFieldAgainstQuery(i18n._(d.title), q);
      score +=
        WEIGHTS.shortTitle * scoreFieldAgainstQuery(i18n._(d.shortTitle), q);
      score +=
        WEIGHTS.whenToUse * scoreFieldAgainstQuery(i18n._(d.whenToUse), q);
      for (const kw of d.keywords) {
        score += WEIGHTS.keyword * scoreFieldAgainstQuery(kw, q);
      }
      for (const syn of d.synonyms) {
        score += WEIGHTS.synonym * scoreFieldAgainstQuery(syn, q);
      }
      // Multiplicative popularity bias. Multiplier IS the popularity value.
      // Decisive enough to flip an ambiguous fuzzy match — "pro" matches more
      // intent text in profileDataImport than productAllInOneImport on raw
      // scoring, but product is far more commonly imported, so popularity 3
      // tips it. Default 1 leaves niche importers unaffected.
      score *= d.popularity ?? 1;
      return { descriptor: d, score };
    });

    return scored
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((s) => s.descriptor);
  }, [query, active, i18n]);
}
